import { type InfiniteQueryKeyData, type InfiniteQueryParams, useOffsetInfinitePagination } from '@pba/react-query';
import { type MedicalGroupList, createMedicalGroupList } from '#models';
import { useOffsetInfiniteQuery } from '#query';
import {
  type MedicalGroupsListQueryFilter,
  type MedicalGroupsListQuerySorter,
  getMedicalGroupsInfiniteQueryKey,
} from '#query-keys/medical-groups';
import type { PaginationExtendedData } from '#types';
import { createPaginationData } from '#utils';
import { getMedicalGroups } from '../../services';
import { useGetMedicalGroupsParameters } from './hooks';

type GetMedicalGroupsInfiniteQueryParams = InfiniteQueryParams<
  MedicalGroupsInfiniteQueryData,
  MedicalGroupsListQueryFilter,
  MedicalGroupsListQuerySorter,
  number
>;

type MedicalGroupsInfiniteQueryData = InfiniteQueryKeyData<
  PaginationExtendedData<MedicalGroupList>,
  typeof getMedicalGroupsInfiniteQueryKey
>;

export const useMedicalGroupsInfiniteQuery = ({
  filter,
  options,
  pagination,
  sorter,
}: GetMedicalGroupsInfiniteQueryParams) => {
  const getMedicalGroupsParameters = useGetMedicalGroupsParameters();
  const getOffsetPagination = useOffsetInfinitePagination();

  return useOffsetInfiniteQuery({
    async queryFn({ pageParam }) {
      const { data } = await getMedicalGroups({
        config: {
          params: getMedicalGroupsParameters({
            filter,
            pagination: getOffsetPagination({ offset: pageParam, pagination }),
            sorter,
          }),
        },
      });

      return createPaginationData({
        createItem: createMedicalGroupList,
        pagination: data,
      });
    },
    queryKey: getMedicalGroupsInfiniteQueryKey({ filter, pagination, sorter }),
    ...options,
  });
};
